import React from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import ZenScoreGauge from "./ZenScoreGauge";

const CATEGORIES = [
  { label: "Cash Flow Health", pct: 82, note: "Consistent net-positive weeks" },
  { label: "Revenue Consistency", pct: 76, note: "Stable monthly volume" },
  { label: "Balance Health", pct: 71, note: "Healthy average daily balance" },
  { label: "Business Stability", pct: 84, note: "Steady operating tenure" },
];

/**
 * ScoreAnatomy — the "under-the-hood" view of a ZenScore breakdown.
 * Big gauge on the left, 4 sub-scores on the right with animated bars.
 * Explicitly a demo mock (has data-testid="zenscore-anatomy-mock" so scanners
 * can exclude the illustrative percentages from marketing-stat checks).
 */
export const ScoreAnatomy = ({ className }) => {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-3xl border border-white/[0.06] bg-bg-card p-8 sm:p-12",
        className,
      )}
      data-testid="zenscore-anatomy-mock"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full blur-3xl"
        style={{
          background:
            "radial-gradient(circle, rgba(0,230,118,0.16) 0%, rgba(0,0,0,0) 65%)",
        }}
      />

      <div className="relative grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center">
        <div className="flex flex-col items-center gap-4 lg:col-span-5">
          <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-brand-emerald">
            Score anatomy · sample
          </span>
          <ZenScoreGauge
            value={782}
            size={240}
            caption="Strong Funding Readiness"
          />
        </div>

        <div className="lg:col-span-7">
          <h3 className="font-display text-2xl font-semibold text-white sm:text-3xl">
            Four categories, one composite view.
          </h3>
          <p className="mt-2 text-sm text-text-secondary">
            Each ZenScore is composed from four category sub-scores, giving you
            transparency into the specific dimensions of business health that
            drive your total number.
          </p>

          <ul className="mt-8 flex flex-col gap-5">
            {CATEGORIES.map((c, i) => (
              <li key={c.label}>
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-sm font-medium text-white">
                    {c.label}
                  </span>
                  <span className="font-mono text-xs tabular-nums text-brand-emerald">
                    {c.pct}%
                  </span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/[0.05]">
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: `${c.pct}%` }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{
                      duration: 1.2,
                      delay: 0.15 * i,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className="h-full rounded-full"
                    style={{
                      background:
                        "linear-gradient(90deg, #00C853 0%, #00E676 100%)",
                      boxShadow: "0 0 12px rgba(0,230,118,0.35)",
                    }}
                  />
                </div>
                <p className="mt-1.5 text-xs text-text-muted">{c.note}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ScoreAnatomy;
